// ResetPasswordPage.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

function getParamsFromUrl() {
  // Bei HashRouter: "#/reset-password?token=...&lang=en"
  const href = window.location.href;

  // 1) Query im Hash
  const hashPart = href.split('#')[1] || '';
  const hashQuery = hashPart.includes('?') ? hashPart.split('?')[1] : '';

  // 2) Fallback: normale Query (ohne Hash)
  const normalQuery = window.location.search ? window.location.search.slice(1) : '';

  return new URLSearchParams(hashQuery || normalQuery);
}

function ResetPasswordPage() {
  const { t, i18n } = useTranslation();

  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const params = getParamsFromUrl();

    const tokenFromUrl = params.get('token');
    if (tokenFromUrl) setToken(tokenFromUrl);

    const lang = params.get('lang');
    if (lang && (lang === 'de' || lang === 'en')) {
      i18n.changeLanguage(lang);
    }
  }, [i18n]);

  const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');
  setMsg('');

  const cleanToken = (token || '').trim();
  if (!cleanToken) {
    setError(t('resetPassword.errors.missingToken', 'Token fehlt.'));
    return;
  }
  if (!newPassword) {
    setError(t('resetPassword.errors.missingPassword', 'Bitte neues Passwort eingeben.'));
    return;
  }

  try {
    const res = await axios.post(
      '/reset-password',
      { token: cleanToken, newPassword, password: newPassword },
      { withCredentials: true }
    );

    const okMsg =
      typeof res.data === 'string'
        ? res.data
        : (res.data?.message || t('resetPassword.success', 'Passwort wurde geändert.'));

    setMsg(okMsg);

    // ✅ Onboarding soll danach KEIN Passwort mehr abfragen
    try {
      sessionStorage.setItem('ONBOARDING_SKIP_PASSWORD', '1');
    } catch {}
  } catch (err) {
    const apiMsg =
      err.response?.data?.error ||
      err.response?.data?.message ||
      (typeof err.response?.data === 'string' ? err.response.data : null);

    setError(apiMsg || t('resetPassword.errors.generic', 'Fehler beim Zurücksetzen.'));
  }
};

  return (
    <div className="rpPage">
      <style>{`
        .rpPage{
          min-height:100vh;
          display:flex;
          align-items:center;
          justify-content:center;
          padding:28px;
          background:
            radial-gradient(1000px 600px at 20% 10%, rgba(152,255,152,0.08), transparent 55%),
            radial-gradient(900px 550px at 90% 30%, rgba(255,255,255,0.06), transparent 60%),
            #0b0f0c;
          font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, "Apple Color Emoji","Segoe UI Emoji";
          color:#e8f5ec;
        }

        .rpCard{
          width:100%;
          max-width:520px;
          border-radius:18px;
          background: rgba(17, 24, 20, 0.82);
          border: 1px solid rgba(255,255,255,0.10);
          box-shadow:
            0 30px 80px rgba(0,0,0,0.55),
            0 1px 0 rgba(255,255,255,0.04) inset;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          padding:26px 26px 22px;
        }

        .rpHeader{
          margin-bottom:18px;
        }

        .rpTitle{
          margin:0;
          font-size:22px;
          font-weight:700;
          letter-spacing:-0.2px;
          line-height:1.2;
        }

        .rpSub{
          margin:8px 0 0;
          font-size:14px;
          line-height:1.45;
          color: rgba(232,245,236,0.78);
        }

        .rpNotice{
          margin:14px 0 0;
          padding:10px 12px;
          border-radius:12px;
          font-size:14px;
          line-height:1.35;
          border: 1px solid rgba(255,255,255,0.12);
          background: rgba(255,255,255,0.06);
        }

        .rpNoticeError{
          border-color: rgba(255,77,77,0.28);
          background: rgba(255,77,77,0.10);
        }

        .rpNoticeOk{
          border-color: rgba(152,255,152,0.22);
          background: rgba(152,255,152,0.10);
        }

        .rpForm{
          margin-top:18px;
          display:flex;
          flex-direction:column;
          gap:14px;
        }

        .rpField{
          display:flex;
          flex-direction:column;
          gap:8px;
        }

        .rpLabel{
          font-size:13px;
          font-weight:600;
          color: rgba(232,245,236,0.85);
        }

        .rpInput{
          width:100%;
          padding:12px 12px;
          border-radius:12px;
          border: 1px solid rgba(255,255,255,0.14);
          background: rgba(10, 14, 12, 0.70);
          color:#e8f5ec;
          outline:none;
          font-size:14px;
          transition: box-shadow .15s ease, border-color .15s ease, background .15s ease;
        }

        .rpInput::placeholder{
          color: rgba(232,245,236,0.45);
        }

        .rpInput:focus{
          border-color: rgba(152,255,152,0.45);
          box-shadow: 0 0 0 4px rgba(152,255,152,0.12);
          background: rgba(10, 14, 12, 0.85);
        }

        .rpInputReadOnly{
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size:12.5px;
          opacity:0.95;
        }

        .rpButton{
          margin-top:6px;
          width:100%;
          padding:12px 14px;
          border-radius:12px;
          border: 1px solid rgba(255,255,255,0.14);
          background: rgba(255,255,255,0.10);
          color:#e8f5ec;
          font-weight:700;
          font-size:14px;
          cursor:pointer;
          transition: transform .06s ease, background .15s ease, border-color .15s ease, box-shadow .15s ease;
        }

        .rpButtonPrimary{
          background: #98ff98;
          border-color: rgba(152,255,152,0.55);
          color:#0b0f0c;
          box-shadow: 0 12px 28px rgba(152,255,152,0.18);
        }

        .rpButton:hover{
          background: rgba(255,255,255,0.14);
        }

        .rpButtonPrimary:hover{
          background: #a6ffa6;
        }

        .rpButton:active{
          transform: translateY(1px);
        }

        .rpLangRow{
          display:flex;
          justify-content:flex-end;
          gap:10px;
          margin-top:8px;
        }

        .rpLangBtn{
          padding:8px 10px;
          border-radius:12px;
          border: 1px solid rgba(255,255,255,0.14);
          background: rgba(255,255,255,0.06);
          color:#e8f5ec;
          font-weight:700;
          font-size:12px;
          cursor:pointer;
        }

        .rpLangBtn:hover{
          background: rgba(255,255,255,0.10);
        }
      `}</style>

      <div className="rpCard">
        <div className="rpHeader">
          <h2 className="rpTitle">{t('resetPassword.title', 'Passwort zurücksetzen')}</h2>
          <p className="rpSub">
            {t(
              'resetPassword.subtitle',
              'Bitte vergib ein neues Passwort. Der Token wird automatisch aus dem Link übernommen.'
            )}
          </p>

          {error && <div className="rpNotice rpNoticeError">{error}</div>}
          {msg && <div className="rpNotice rpNoticeOk">{msg}</div>}
        </div>

        <form className="rpForm" onSubmit={handleSubmit}>
          <div className="rpField">
            <label className="rpLabel">{t('resetPassword.tokenLabel', 'Token')}</label>
            <input
              className="rpInput rpInputReadOnly"
              type="text"
              value={token}
              readOnly
              placeholder={t('resetPassword.tokenPlaceholder', 'Token aus dem Link')}
            />
          </div>

          <div className="rpField">
            <label className="rpLabel">{t('resetPassword.passwordLabel', 'Neues Passwort')}</label>
            <input
              className="rpInput"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>

          <button className="rpButton rpButtonPrimary" type="submit">
            {t('resetPassword.submit', 'Passwort ändern')}
          </button>

          {/* Optionaler Fallback-Switch (Option 2), falls lang-Param fehlt */}
          <div className="rpLangRow">
            <button className="rpLangBtn" type="button" onClick={() => i18n.changeLanguage('de')}>DE</button>
            <button className="rpLangBtn" type="button" onClick={() => i18n.changeLanguage('en')}>EN</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
